// The product type as one field: the type picked (the way down to it), or the one named instead, in a single
// box — clicked (or Change), the picker opens under it (ProductTypeBrowser: the search, the tree, the "not
// listed" input) and closes again once a type is picked or named.
import React, { useState } from "react"
import { ChevronDownIcon, TagIcon } from "@heroicons/react/24/outline"
import ProductTypeBrowser from "./ProductTypeBrowser"
import { useProductTypes, nameOf } from "./useProductTypes"

/**
 * `value`: the type's slug; `proposal`: a type named instead ({ name, language, parentSlug });
 * `onChange({ slug, node })` / `onPropose(proposal)` as ProductTypeBrowser; `t`: the host's texts.
 */
export default function ProductTypeField({ value = "", proposal = null, onChange, onPropose, t = (s) => s, disabled = false }) {
  const { bySlug } = useProductTypes()
  const [open, setOpen] = useState(!value && !proposal)
  const node = value ? bySlug.get(value) : null
  const parentPath = (slug) => (bySlug.get(slug)?.path || []).map((one) => nameOf(bySlug.get(one))).filter(Boolean)
  const path = node ? parentPath(node.parents?.[0]) : proposal ? parentPath(proposal.parentSlug) : []
  const label = node ? nameOf(node) : proposal?.name || ""

  return (
    <div className="flex flex-col gap-2" data-testid="product-type-field">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`flex items-center gap-3 w-full min-h-[44px] px-3 py-2 rounded-md border bg-admin_paper text-left transition-colors ${open ? "border-admin_primary" : "border-admin_bordered hover:border-admin_primary"}`}
        aria-expanded={open}
        data-testid="product-type-value"
      >
        <TagIcon className="w-5 h-5 shrink-0 text-admin_text_secondary" />
        {label ? (
          <span className="min-w-0 flex-1 text-sm">
            {!!path.length && <span className="text-admin_text_secondary">{path.join(" › ")} › </span>}
            <b>{label}</b>
            {(proposal || node?.status === "draft") && <span className="ml-2 inline-flex items-center h-5 px-2 rounded-full bg-admin_warning/15 text-admin_warning text-[11px] font-semibold">{t`In review`}</span>}
          </span>
        ) : (
          <span className="min-w-0 flex-1 text-sm text-admin_text_secondary">{t`Pick a product type`}</span>
        )}
        <span className="shrink-0 text-xs font-semibold text-admin_primary">{label ? t`Change` : ""}</span>
        <ChevronDownIcon className={`w-4 h-4 shrink-0 text-admin_text_secondary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="rounded-md border border-admin_bordered bg-admin_neutral_1/30 p-3">
          <ProductTypeBrowser
            value={value}
            proposal={proposal}
            t={t}
            onChange={(picked) => {
              onChange?.(picked)
              setOpen(false)
            }}
            onPropose={(named) => {
              onPropose?.(named)
              setOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
